import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Typography } from "@mui/material";
import { getAgeDistribution } from "../../api/region";
import { useSelector } from "react-redux";

const AgeDistribution = () => {
  const [ageDistributionData, setAgeDistributionData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { filter } = useSelector((state) => state);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await getAgeDistribution(filter);
      setAgeDistributionData(data || []);
      setIsLoading(false);
    } catch (err) {
      console.error("Error while loading user data", err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filter)]);

  // 🔹 Map API response into Highcharts format
  const categories = ageDistributionData.map((item) => item.ageGroup);
  const maleData = ageDistributionData.map((item) => item.Male || 0);
  const femaleData = ageDistributionData.map((item) => item.Female || 0);
  const otherData = ageDistributionData.map((item) => item.Other || 0);

  // 🔹 Totals for legend
  const totals = {
    Male: maleData.reduce((a, b) => a + b, 0),
    Female: femaleData.reduce((a, b) => a + b, 0),
    "Other / Not specified": otherData.reduce((a, b) => a + b, 0),
  };

  const options = {
    chart: {
      type: "bar",
      backgroundColor: "#fff",
      height: 300,
      // events: {
      //   render() {
      //     if (!this.hasStackLabelsFixed) {
      //       this.hasStackLabelsFixed = true;
      //       setTimeout(() => this.redraw(true), 150);
      //     }
      //   },
      // },
      events: {
        render() {
          if (!this.hasStackLabelsFixed) {
            this.hasStackLabelsFixed = true;
            setTimeout(() => {
              if (this.legend) this.redraw(true);
            }, 150);
          }
        },
      },

    },
    title: {
      text: "Age Distribution",
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: "500",
        color: "#3C4043",
        fontFamily: "Indivisible, sans-serif",
      },
    },
    xAxis: {
      categories,
      title: { text: null },
      labels: { style: { fontSize: "12px" } },
    },
    yAxis: {
      min: 0,
      title: { text: null },
      labels: { enabled: false },
      gridLineWidth: 0,
      stackLabels: {
        enabled: true,
        formatter: function () {
          let categoryTotal = 0;
          this.axis.series.forEach((series) => {
            if (series.data[this.x]) {
              categoryTotal += series.data[this.x].y || 0;
            }
          });

          const grandTotal =
            Object.values(totals).reduce((a, b) => a + b, 0) || 1;
          const percentage = ((categoryTotal / grandTotal) * 100).toFixed(1);

          return `${categoryTotal.toLocaleString()} (${percentage}%)`;
        },
        style: {
          fontWeight: "bold",
          color: "#000",
          textOutline: "none",
        },
      },
    },
    legend: {
      enabled: true,
      reversed: true,
      symbolRadius: 4,
      itemStyle: { fontSize: "14px" },
      align: "left",
      verticalAlign: "top",
      labelFormatter: function () {
        return `${this.name}: ${totals[this.name].toLocaleString()}`;
      },
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.series.name}</b><br/>${this.point.category}: ${this.y.toLocaleString()} users`;
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: { enabled: false },
      },
    },
    series: [
      { name: "Other / Not specified", data: otherData, color: "#9747FF" },
      { name: "Female", data: femaleData, color: "#7EC5FF" },
      { name: "Male", data: maleData, color: "#2E80DB" },
    ],
    credits: { enabled: false },
  };

  return (
    <Box
      sx={{
        boxShadow: "none",
        border: "1px solid #e6e6e6",
        borderRadius: "20px",
        p: 2,
      }}
    >
      {(isLoading || !Object?.values(totals)?.reduce((curr, acc) => acc + curr, 0))  ? (
        <Box>
            <Typography fontWeight={500} mb={2} sx={{ fontSize: '20px', color: '#3C4043' }}>
              Age Distribution
            </Typography>

            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
               {isLoading ? "Loading..." : "No data available"}
              </Typography>
            </Box>
          </Box>
      ) : (<HighchartsReact highcharts={Highcharts} options={options} />)}
    </Box>
  );
};

export default AgeDistribution;
